import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Dialog, DialogContent, Button } from '@material-ui/core'
import { Add, CloudUpload, History, Lock, LockOpen } from '@material-ui/icons'
import { Image, Spinner } from 'ui'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import useStore from 'store'
import { getProtocolPermissions } from './permissions'
import { cleanImportedText, htmlToPlainText } from './textUtils'
import ProtocolStatusHistoryView from './ProtocolStatusHistoryView'

import {
  DialogHeaderBlue,
  Header,
  StyledClose,
  StyledIconButton,
  StyledIconButtonWhite,
  CategoryListTitleContainer,
  CategoryListTitleSubContainer1,
  HeaderTitle1,
  NewCategoryBody,
  NewCategoryContainerColumn,
  NewCategoryContainer,
  NewSubCategoryContainer,
} from './CategoryModal.styles'
import { FormInput, FormSelect, FormSearch, UserOption } from './FormComponents'

import ApprovedStatusIcon from 'assets/icons/protocols/icon10.svg'
import RemoveIcon from 'assets/icons/protocols/icon7.svg'
import DocIcon from 'assets/icons/protocols/icon12.svg'
import PrintIcon from 'assets/icons/protocols/icon14.svg'
import AyeIcon from 'assets/icons/protocols/icon25.svg'
import SaveIcon from 'assets/icons/protocols/icon26.svg'
import PendingApproveIcon from 'assets/icons/protocols/icon28.svg'
import DraftStatusIcon from 'assets/icons/protocols/icon29.svg'
import Doc2Icon from 'assets/icons/protocols/icon19.svg'
import SecondaryFileIcon from 'assets/icons/protocols/icon23.svg'
import DeleteIcon from 'assets/icons/ims/Delete.svg'

// ─── styled components ────────────────────────────────────────────────────────

export const CategoryListTitleSubContainer2 = styled.div`
  display: flex;
  width: 48px;
  height: 48px;
  padding: 12px;
  flex-direction: column;
  align-items: flex-start;
  flex-shrink: 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.20);
`
export const HeaderTitle2 = styled.div`
  display: flex;
  color: #F3E8FF;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`
export const HeaderDialogButton = styled(Button)`
  display: flex;
  height: 39.986px;
  margin-right: 10px;
  padding-left: 15.994px;
  align-items: center;
  gap: 7.997px;
  flex-shrink: 0;
  border-radius: 8px;
  background: #9810FA;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.10), 0 2px 4px -2px rgba(0, 0, 0, 0.10);
  color: #FFF;
  text-align: center;
  font-family: Rubik;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
`
export const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`
export const ImageHeaderBlue = styled(Image)`
  padding-left: 5px;
`
// Recolors the icon to solid white to match HeaderDialogButton's text color (#FFF) -
// ApprovedStatusIcon is otherwise a colored (green) icon meant for the status card, not this button.
const ImageHeaderWhite = styled(ImageHeaderBlue)`
  filter: brightness(0) invert(1);
`
export const ImageHeader = styled(Image)`
  display: flex;
`
export const ProtocolEditContainer = styled.div`
  border-radius: 8px;
  padding: 32px 50px;
  border: 0.909px solid #E5E7EB;
  background: #FFF;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px -1px rgba(0, 0, 0, 0.10);
`
export const CloseButton = styled(Button)`
  border-radius: 8px;
  background: #E5E7EB;
  display: flex;
  padding: 8px 24px;
  justify-content: center;
  align-items: center;
  color: #364153;
  text-align: center;
  font-family: Rubik;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
`
export const ProtocolContainerActions = styled.div`
  border-top: 0.909px solid #E5E7EB;
  width: 100%;
  display: flex;
  height: 58.707px;
  justify-content: flex-end;
  align-items: center;
  gap: 11.989px;
  flex-shrink: 0;
  align-self: stretch;
`
export const SpinnerWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px 0;
  width: 100%;
`
const StyledDateInput = styled.input`
  display: flex;
  height: 41.804px;
  padding: 8px 16px;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 8px;
  border: 0.909px solid #D1D5DC;
  color: rgba(15, 23, 42, 0.70);
  text-align: right;
  font-family: Rubik;
  font-size: 16px;
  font-weight: 400;
  outline: none;
  width: 100%;
  box-sizing: border-box;
`
export const FieldLabel = styled.div`
  color: #364153;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: right;
  width: 100%;
`
export const FieldRequired = styled.span`
  color: #DC2626;
  margin-left: 2px;
`
export const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  align-self: stretch;
  width: 100%;
`
export const StyledTextarea = styled.textarea`
  display: flex;
  width: 100%;
  min-height: 120px;
  padding: 12px 16px;
  border-radius: 8px;
  border: 0.909px solid #D1D5DC;
  color: rgba(15, 23, 42, 0.70);
  text-align: right;
  font-family: Rubik;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.6;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
  /* @noflip */ direction: rtl;
`
const SectionLabel = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-start;
  align-items: center;
  color: #101828;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  /* @noflip */ direction: rtl;
`
const ListSectionLabel = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  color: #101828;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  /* @noflip */ direction: rtl;
`
const AddRowButton = styled(Button)`
  color: #155DFC;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 500;
  padding: 0;
  min-width: 0;
  text-transform: none;
`
const ListRow = styled.div`
  display: flex;
  width: 100%;
  gap: 12px;
  align-items: center;
  /* @noflip */ direction: rtl;
`
const RowNumber = styled.div`
  display: flex;
  width: 24px;
  height: 24px;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background: #EFF6FF;
  color: #155DFC;
  font-family: Rubik;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
`
// File section
const FileSectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #101828;
  font-family: Rubik;
  font-size: 15px;
  font-weight: 600;
  /* @noflip */ direction: rtl;
  width: 100%;
`
const MainFileCard = styled.div`
  display: flex;
  width: 100%;
  padding: 16px 20px;
  justify-content: space-between;
  align-items: center;
  border-radius: 8px;
  border: 1.818px solid #51A2FF;
  background: linear-gradient(90deg, #EFF6FF 0%, #DBEAFE 100%);
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px -1px rgba(0, 0, 0, 0.10);
  /* @noflip */ direction: rtl;
`
const MainFileInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  flex: 1;
`
const MainFileName = styled.div`
  color: #101828;
  font-family: Rubik;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
`
const MainFileSize = styled.div`
  color: #364153;
  font-family: Rubik;
  font-size: 13px;
  font-weight: 400;
`
const MainFileIconBox = styled.div`
  display: flex;
  width: 40px;
  height: 40px;
  padding: 10px;
  margin-left: 12px;
  flex-direction: column;
  align-items: flex-start;
  flex-shrink: 0;
  border-radius: 8px;
`
const SecondaryFileCard = styled.div`
  display: flex;
  width: 100%;
  padding: 12px 16px;
  justify-content: space-between;
  align-items: center;
  border-radius: 8px;
  border: 0.909px solid #E5E7EB;
  background: #F9FAFB;
  /* @noflip */ direction: rtl;
  box-sizing: border-box;
`
const SecondaryFileInfo = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`
const SecondaryFileName = styled.div`
  color: #101828;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 500;
`
const SecondaryFileSize = styled.div`
  color: #64748B;
  font-family: Rubik;
  font-size: 12px;
`
const SecondaryFileIconBox = styled.div`
  display: flex;
  width: 32px;
  height: 32px;
  padding: 8px;
  flex-shrink: 0;
  border-radius: 6px;
  margin-left: 8px;
`
const UploadZone = styled.label`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px 16px;
  border-radius: 8px;
  border: 1.5px dashed #D1D5DC;
  background: #F8FAFC;
  cursor: pointer;
  box-sizing: border-box;
  &:hover {
    border-color: #155DFC;
    background: #EFF6FF;
  }
`
const UploadText = styled.div`
  color: #64748B;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 400;
  text-align: center;
`

const HiddenInput = styled.input`
  display: none;
`
// Private / Public bar at the bottom of the edit form. Grey button = private,
// blue button = public (IsPublic).
const VisibilityBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  margin-top: 24px;
  padding: 16px;
  box-sizing: border-box;
  border-radius: 8px;
  background: #F3F4F6;
`
const VisibilityText = styled.div`
  color: #45556C;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: right;
`
const VisibilityButton = styled(Button)<{ $public: boolean }>`
  && {
    min-width: 96px;
    padding: 8px 16px;
    border-radius: 8px;
    font-family: Rubik;
    font-size: 14px;
    font-weight: 600;
    text-transform: none;
    color: ${p => (p.$public ? '#FFFFFF' : '#314158')};
    background: ${p => (p.$public ? '#155DFC' : '#CAD5E2')};
  }
  &&:hover {
    background: ${p => (p.$public ? '#0F4FDB' : '#B8C4D6')};
  }
`
const NewBadge = styled.span`
  display: inline-flex;
  padding: 2px 8px;
  color: #008236;
  font-family: Rubik;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  flex-shrink: 0;
`
const ActionsImage = styled(Image)`
  width: 16px;
  height: 16px;
`
export const StatusTitle = styled.div`
  display: flex;
  width:100%;
  padding: 0 1px 0 1px;
  justify-content: flex-start;
  align-items: flex-end;
  gap: 3.082px;
  align-self: stretch;
`
// Row holding the status-section title and the audit (status-log) button.
export const StatusHeaderRow = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`
// Shown in the Approve/Reject screen when the protocol isn't currently Pending
// Approval (e.g. still Draft) - approving/rejecting is disabled in that state.
export const NotPendingApprovalNotice = styled.div`
  align-self: stretch;
  color: #E7000B;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: right;
`
// Locks every field in the full-edit screen once the protocol is Approved (published) -
// simpler and more robust than threading a disabled prop through every individual field.
// The body lays its children out with a gap of its own, so the form sections sit
// inside one box: the lock is split in two around the status row, and without
// this the split would open a gap above and below it.
const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`
const LockableFields = styled.div<{ $locked: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${p => p.$locked && `
    pointer-events: none;
    opacity: 0.55;
  `}
`
// Blue-framed "watch the status-change log" (audit) button — mirrors the one
// in the protocol View modal.
const StatusHistoryButton = styled(Button)`
  gap: 6px;
  padding: 6px 14px;
  font-size: 14px;
  border-radius: 8px;
  border: 1.818px solid #3B82F6;
  background: rgba(59, 130, 246, 0.08);
  color: #3B82F6;
  font-family: Rubik;
  font-weight: 500;
  line-height: 20px;
  text-transform: none;
  white-space: nowrap;
  flex-shrink: 0;
  &:hover { background: rgba(59, 130, 246, 0.16); }
`
export const StatusContent = styled.div<{ $compact?: boolean; $spread?: boolean }>`
  display: flex;
  width: 100%;
  justify-content: ${p => !p.$compact ? 'center' : p.$spread ? 'space-between' : 'flex-start'};
  align-items: ${p => p.$compact ? 'center' : 'flex-start'};
  gap: 11.989px;
  flex-shrink: 0;
  align-self: stretch;
`
// Holds the publish button at the far end of the status row, next to the
// (compact) status cards.
const StatusPublishSlot = styled.div`
  display: flex;
  align-items: center;
`
export const StatusCard = styled.div<{ selected?: boolean; selectedBg?: string; selectedColor?: string; selectedBorder?: string; disabled?: boolean; $compact?: boolean }>`
  display: flex;
  flex: ${p => p.$compact ? '0 0 auto' : '1'};
  /* Compact cards share one width so every status button is the same size. */
  min-width: ${p => p.$compact ? '170px' : 'auto'};
  height: ${p => p.$compact ? '38px' : '51.605px'};
  padding: ${p => p.$compact ? '6px 14px' : '8px 16px'};
  justify-content: center;
  align-items: center;
  gap: 7.679px;
  border-radius: 8px;
  border: 1.5px solid ${p => p.disabled ? '#E5E7EB' : p.selected ? p.selectedBorder : '#D1D5DC'};
  background: ${p => p.disabled ? '#F9FAFB' : p.selected ? p.selectedBg : '#fff'};
  color: ${p => p.disabled ? '#C0C7D0' : p.selected ? p.selectedColor : '#4C5564'};
  text-align: center;
  font-family: Rubik;
  font-size: ${p => p.$compact ? '14px' : '15px'};
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  white-space: nowrap;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.15s;
  &:hover {
    border: 1.5px solid ${p => p.disabled ? '#E5E7EB' : p.selectedBorder};
  }
`

// ─── helpers ─────────────────────────────────────────────────────────────────

const formatFileSize = (bytes: number): string => {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const parseList = (val?: string): string[] => {
  const arr = (val || '').split(/[;\n]/).map(s => s.trim()).filter(Boolean)
  return arr.length ? arr : ['']
}

const toDateInputValue = (isoString: string): string => {
  if (!isoString) return ''
  return isoString.substring(0, 10)
}

// ─── component ───────────────────────────────────────────────────────────────

const ProtocolEditModal = ({ open, protocolId, onClose, onSaved }: { open: boolean; protocolId: number | null; onClose: () => void; onSaved?: () => void }) => {
  const { t } = useTranslation('protocols')
  const { orgs, protocolsStore } = useStore()
  const data = protocolsStore.protocolEdit
  const loading = protocolsStore.loadingProtocolEdit
  const perms = getProtocolPermissions(protocolsStore.userPermissions)
  // Published protocol is final - the full edit screen (not the Approve/Reject screen,
  // which has its own Draft/Approved notices) locks every field once already Approved.
  const isLocked = data?.protocolStatusId === 3
  // A protocol that is out for approval is not the manager's to rewrite underneath
  // the people reviewing it. Pull it back to Draft first and the fields open up -
  // the status card does that straight away, so it is one trip through the screen.
  const isPendingApproval = data?.protocolStatusId === 2

  const [title, setTitle] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [date, setDate] = useState('')
  const [participants, setParticipants] = useState<UserOption[]>([])
  const [topics, setTopics] = useState<string[]>([''])
  const [decisions, setDecisions] = useState<string[]>([''])
  const [description, setDescription] = useState('')
  const [protocolStatusId, setProtocolStatusId] = useState(1)
  const [isPublic, setIsPublic] = useState(false)
  const [deletedFileIds, setDeletedFileIds] = useState<number[]>([])
  const [newMainFile, setNewMainFile] = useState<File | null>(null)
  const [newAttachments, setNewAttachments] = useState<File[]>([])
  const [saving, setSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [showStatusHistory, setShowStatusHistory] = useState(false)

  useEffect(() => {
    setShowStatusHistory(false)
  }, [protocolId])

  const mainFileRef = useRef<HTMLInputElement>(null)
  const attachmentsRef = useRef<HTMLInputElement>(null)

  // The protocol can only have one main file - picking a new one without explicitly
  // deleting the current one first replaces it (marks the old one for deletion too).
  const replaceMainFile = (file: File) => {
    setNewMainFile(file)
    if (data?.mainFile) {
      setDeletedFileIds(prev => prev.includes(data.mainFile!.id) ? prev : [...prev, data.mainFile!.id])
    }
  }

  useEffect(() => {
    if (open) {
      protocolsStore.loadUserList()
      protocolsStore.loadCategoryNames()
    }
  }, [open])

  useEffect(() => {
    if (data) {
      setTitle(data.title || '')
      setCategoryId(data.categoryId ? String(data.categoryId) : '')
      setDate(toDateInputValue(data.protocolDate))
      setParticipants(
        data.participants.map(p => ({ userId: p.userId, fullName: p.participantName }))
      )
      setTopics(parseList(data.topics))
      setDecisions(parseList(data.decisions))
      // Imported protocols can hold an HTML fragment here while the form (and
      // the rest of the app) treats the content as plain text - convert it so
      // the editor shows readable text instead of markup. Saving then persists
      // the plain-text version.
      setDescription(htmlToPlainText(data.description))
      setProtocolStatusId(data.protocolStatusId || 1)
      setIsPublic(data.isPublic ?? false)
      setDeletedFileIds([])
      setNewMainFile(null)
      setNewAttachments([])
    }
  }, [data])

  const handleClose = () => {
    setShowStatusHistory(false)
    onClose()
  }

  const updateTopic = (index: number, value: string) => setTopics(prev => prev.map((t, i) => i === index ? value : t))
  const addTopic = () => setTopics(prev => [...prev, ''])
  const removeTopic = (index: number) => setTopics(prev => prev.filter((_, i) => i !== index))

  const updateDecision = (index: number, value: string) => setDecisions(prev => prev.map((d, i) => i === index ? value : d))
  const addDecision = () => setDecisions(prev => [...prev, ''])
  const removeDecision = (index: number) => setDecisions(prev => prev.filter((_, i) => i !== index))

  const handleSave = async () => {
    if (!protocolId) return
    setSaving(true)
    try {
      // Published protocol: only the visibility can change. Everything else is sent exactly
      // as it was loaded (not from the form state, which e.g. converts imported HTML to text),
      // and no files are touched - the server ignores all but isPublic anyway.
      const res = await protocolsStore.updateProtocol(protocolId, isLocked && data ? {
        title: data.title,
        protocolDate: data.protocolDate,
        newCategoryId: data.categoryId,
        protocolStatusId: data.protocolStatusId,
        participants: data.participants.map(p => ({ userId: p.userId, participantRole: p.participantRole || '' })),
        topics: data.topics,
        decisions: data.decisions,
        description: data.description,
        isPublic,
      } : {
        title,
        protocolDate: date ? new Date(date).toISOString() : '',
        newCategoryId: Number(categoryId),
        protocolStatusId,
        participants: participants.map(p => ({
          userId: p.userId,
          participantRole: data?.participants.find(dp => dp.userId === p.userId)?.participantRole || '',
        })),
        topics: topics.map(topic => topic.trim()).filter(Boolean).join(';'),
        decisions: decisions.map(d => d.trim()).filter(Boolean).join(';'),
        description,
        isPublic,
      })

      if (res) {
        // Status here can only ever be Draft/Pending Approval (see the status cards above) -
        // already persisted by the general update call. Approve/Reject is a separate screen
        // (ProtocolEditStatusModal) that calls the dedicated /status endpoint directly.
        const filesToUpload = isLocked ? [] : [
          ...(newMainFile ? [{ file: newMainFile, isMainFile: true }] : []),
          ...newAttachments.map(f => ({ file: f, isMainFile: false })),
        ]
        let filesOk = true
        if (!isLocked && (deletedFileIds.length > 0 || filesToUpload.length > 0)) {
          filesOk = !!(await protocolsStore.deleteProtocolFiles(protocolId, deletedFileIds, filesToUpload))
        }
        if (filesOk) {
          handleClose()
          onSaved?.()
        }
      }
    } catch (err) {
      console.error('ProtocolEditModal.handleSave failed', err)
    } finally {
      setSaving(false)
    }
  }

  const handlePublish = async () => {
    if (!protocolId) return
    setPublishing(true)
    try {
      const res = await protocolsStore.publishProtocol(protocolId)
      if (res) {
        handleClose()
        onSaved?.()
      }
    } catch (err) {
      console.error('ProtocolEditModal.handlePublish failed', err)
    } finally {
      setPublishing(false)
    }
  }

  const modifiedStr = data?.modifiedDate ? new Date(data.modifiedDate).toLocaleDateString('he-IL') : ''
  const protocolName = cleanImportedText(data?.title)

  // Publishing is offered twice - beside the status block and in the footer - so
  // the condition, the handler and the disabled rule live in one place.
  // Everything except the status row, the status log and Publish - the three things
  // that stay available while the protocol waits for approval.
  const fieldsLocked = isLocked || (isPendingApproval && protocolStatusId !== 1)

  const canPublishProtocol = !!data && perms.canPublish(Number(categoryId))
    && (data.protocolStatusId === 2 || data.protocolStatusId === 4) && data.canPublish
  const renderPublishButton = () => (
    <HeaderDialogButton onClick={handlePublish} disabled={publishing || saving}>
      {publishing ? <Spinner size={16} /> : <><ImageHeaderWhite src={ApprovedStatusIcon} /> {t`publish`}</>}
    </HeaderDialogButton>
  )

  return (
    <Dialog style={{ zIndex: 1200 }} open={open} fullWidth maxWidth="md" scroll="body">
      {showStatusHistory && protocolId ? (
        <ProtocolStatusHistoryView
          protocolId={protocolId}
          protocolTitle={protocolName}
          protocolDate={data?.protocolDate}
          onBack={() => setShowStatusHistory(false)}
        />
      ) : (
      <>
      <DialogHeaderBlue>
        <Header style={{ background: 'linear-gradient(90deg, #9810FA 0%, #8200DB 100%)' }}>
          <CategoryListTitleContainer>
            <CategoryListTitleSubContainer2>
              <ImageHeader src={DocIcon} />
            </CategoryListTitleSubContainer2>
            <CategoryListTitleSubContainer1>
              <HeaderTitle1 style={{ color: 'white' }}>
                {protocolName || t`edit_title`}
              </HeaderTitle1>
              <HeaderTitle2>
                {data?.version && <MetaItem>{t`version`} {data.version} • </MetaItem>}
                {modifiedStr && <MetaItem>{t`last_updated`} {modifiedStr}</MetaItem>}
              </HeaderTitle2>
            </CategoryListTitleSubContainer1>
          </CategoryListTitleContainer>
          {/*<>
          <HeaderDialogButton><ImageHeaderBlue src={AyeIcon} /> {t`view_protocol`}</HeaderDialogButton>
          <HeaderDialogButton><ImageHeaderBlue src={PrintIcon} /> {t`share_protocol`}</HeaderDialogButton>
          </>*/}
          <StyledIconButtonWhite onClick={handleClose}><StyledClose /></StyledIconButtonWhite>
        </Header>
      </DialogHeaderBlue>

      <DialogContent>
        <ProtocolEditContainer>
          {loading && <SpinnerWrap><Spinner /></SpinnerWrap>}

          {!loading && data && (
            <NewCategoryBody>

              {isLocked && (
                <NotPendingApprovalNotice>{t`notice_published_visibility_only`}</NotPendingApprovalNotice>
              )}

              {fieldsLocked && !isLocked && (
                <NotPendingApprovalNotice>{t`notice_pending_return_to_draft`}</NotPendingApprovalNotice>
              )}

              <FormColumn>
              <LockableFields $locked={fieldsLocked}>

              {/* Title */}
              <NewCategoryContainer>
                <NewSubCategoryContainer style={{ height: 'auto', width: '100%' }}>
                  <FormInput
                    label={t`field_title`}
                    required
                    value={title}
                    onChange={setTitle}
                    placeholder={t`title_placeholder_month`}
                  />
                </NewSubCategoryContainer>
              </NewCategoryContainer>

              {/* Category + Date */}
              <NewCategoryContainer style={{ marginTop: '16px' }}>
                <NewSubCategoryContainer style={{ height: 'auto', width: '50%' }}>
                  <FormSelect
                    label={t`category`}
                    required
                    value={categoryId}
                    onChange={setCategoryId}
                    disabled={protocolStatusId !== 1}
                    options={[
                      { value: '', label: t`choose_category` },
                      ...protocolsStore.categoryNames.map(c => ({ value: String(c.id), label: cleanImportedText(c.categoryName) })),
                    ]}
                  />
                </NewSubCategoryContainer>
                <NewSubCategoryContainer style={{ height: 'auto' }}>
                  <FieldWrapper>
                    <FieldLabel>{t`field_date`}<FieldRequired>*</FieldRequired></FieldLabel>
                    <StyledDateInput
                      type="date"
                      value={date}
                      max={new Date().toISOString().split('T')[0]}
                      onChange={e => setDate(e.target.value)}
                    />
                  </FieldWrapper>
                </NewSubCategoryContainer>
              </NewCategoryContainer>

              </LockableFields>

              {/* Outside the lock on purpose: returning to Draft, reading the status
                  log and publishing are what this screen still allows while the
                  protocol is pending. */}
              <NewCategoryContainer style={{ marginTop: '16px', borderRadius: '8px', border: '2px solid #BEDBFF', background: '#EFF6FF', flexDirection: 'column', gap: '8px', padding: '12px' }}>
                <StatusHeaderRow>
                  <StatusTitle>{t`protocol_status`}</StatusTitle>
                  {perms.canViewStatusHistory(data.categoryId) && (
                    <StatusHistoryButton onClick={() => setShowStatusHistory(true)}><History style={{ fontSize: 18 }} /> {t`status_change_log`}</StatusHistoryButton>
                  )}
                </StatusHeaderRow>
                <StatusContent $compact $spread={canPublishProtocol}>
                  {/* This screen (UpdateProtocol endpoint) only ever moves the protocol between
                      Draft and Pending Approval. Approve/Reject is a separate action, with its own
                      icon/screen (ProtocolEditStatusModal) and its own endpoint (ApproveOrRejectProtocol). */}
                  {[
                    { titleKey: 'status_draft', value: 1, icon: DraftStatusIcon, selectedBg: '#f6f3f4', selectedColor: '#101828', selectedBorder: '#6a7282' },
                    { titleKey: 'status_pending', value: 2, icon: PendingApproveIcon, selectedBg: '#FEF9C2', selectedColor: '#733E0A', selectedBorder: '#F0B100' },
                  ].map(item => {
                    // Category Manager, Main Manager or Admin - the pencil icon itself is already
                    // gated server-side (isEditAll), this is a defensive second check.
                    const isDisabled = !(perms.isCategoryManagerOf(Number(categoryId)) || perms.isMainManager || perms.isAdmin)

                    return (
                      <StatusCard
                        key={item.value}
                        selected={protocolStatusId === item.value}
                        selectedBg={item.selectedBg}
                        selectedColor={item.selectedColor}
                        selectedBorder={item.selectedBorder}
                        disabled={isDisabled}
                        $compact
                        onClick={() => !isDisabled && setProtocolStatusId(item.value)}
                      >
                        <ImageHeader src={item.icon} style={{ width: 18, height: 18 }} />
                        {t(item.titleKey)}
                      </StatusCard>
                    )
                  })}
                  {canPublishProtocol && <StatusPublishSlot>{renderPublishButton()}</StatusPublishSlot>}
                </StatusContent>
              </NewCategoryContainer>

              <LockableFields $locked={fieldsLocked}>

              {/* Participants */}
              <NewCategoryContainerColumn style={{ marginTop: '16px' }}>
                <SectionLabel>{t`participants`}</SectionLabel>
                <FormSearch
                  label=""
                  value={participants}
                  onChange={setParticipants}
                  options={protocolsStore.userList}
                  placeholder={t`search_participant`}
                />
              </NewCategoryContainerColumn>

              {/* Topics */}
              <NewCategoryContainerColumn style={{ marginTop: '16px' }}>
                <ListSectionLabel>
                  <span>{t`topics`}</span>
                  <AddRowButton startIcon={<Add />} onClick={addTopic}>{t`add_topic`}</AddRowButton>
                </ListSectionLabel>
                {topics.map((topic, i) => (
                  <ListRow key={i}>
                    <RowNumber>{i + 1}</RowNumber>
                    <div style={{ flex: 1 }}>
                      <FormInput
                        label=""
                        value={topic}
                        onChange={v => updateTopic(i, v)}
                        placeholder={t`topic_placeholder`}
                      />
                    </div>
                    {topics.length > 1 && (
                      <StyledIconButton onClick={() => removeTopic(i)}>
                        <ActionsImage src={DeleteIcon} />
                      </StyledIconButton>
                    )}
                  </ListRow>
                ))}
              </NewCategoryContainerColumn>

              {/* Decisions */}
              <NewCategoryContainerColumn style={{ marginTop: '16px' }}>
                <ListSectionLabel>
                  <span>{t`decisions`}</span>
                  <AddRowButton startIcon={<Add />} onClick={addDecision}>{t`add_decision`}</AddRowButton>
                </ListSectionLabel>
                {decisions.map((d, i) => (
                  <ListRow key={i}>
                    <RowNumber>{i + 1}</RowNumber>
                    <div style={{ flex: 1 }}>
                      <FormInput
                        label=""
                        value={d}
                        onChange={v => updateDecision(i, v)}
                        placeholder={t`decision_placeholder`}
                      />
                    </div>
                    {decisions.length > 1 && (
                      <StyledIconButton onClick={() => removeDecision(i)}>
                        <ActionsImage src={DeleteIcon} />
                      </StyledIconButton>
                    )}
                  </ListRow>
                ))}
              </NewCategoryContainerColumn>

              {/* Description */}
              <NewCategoryContainerColumn style={{ marginTop: '16px' }}>
                <FieldWrapper>
                  <FieldLabel>{t`field_content`}<FieldRequired>*</FieldRequired></FieldLabel>
                  <StyledTextarea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder={t`content_placeholder_enter`}
                    dir="rtl"
                  />
                </FieldWrapper>
              </NewCategoryContainerColumn>

              {/* Main file */}
              <NewCategoryContainerColumn style={{ marginTop: '24px' }}>
                <FileSectionTitle>
                  <ImageHeader src={DocIcon} style={{ width: 18, height: 18 }} />
                  {t`main_file`}
                </FileSectionTitle>

                {data.mainFile && !newMainFile && !deletedFileIds.includes(data.mainFile.id) && (
                  <MainFileCard>
                    <MainFileIconBox>
                      <ImageHeader src={Doc2Icon} />
                    </MainFileIconBox>
                    <MainFileInfo>
                      <MainFileName>{cleanImportedText(data.mainFile.fileName)}</MainFileName>
                      <MainFileSize>{formatFileSize(data.mainFile.fileSize)}</MainFileSize>
                    </MainFileInfo>
                    <StyledIconButton onClick={() => setDeletedFileIds(prev => [...prev, data.mainFile!.id])}>
                      <ActionsImage src={RemoveIcon} />
                    </StyledIconButton>
                  </MainFileCard>
                )}

                {newMainFile && (
                  <MainFileCard>
                    <MainFileIconBox>
                      <ImageHeader src={Doc2Icon} />
                    </MainFileIconBox>
                    <MainFileInfo>
                      <MainFileName>{newMainFile.name} <NewBadge>{t`badge_new`}</NewBadge></MainFileName>
                      <MainFileSize>{formatFileSize(newMainFile.size)}</MainFileSize>
                    </MainFileInfo>
                    <StyledIconButton onClick={() => {
                      setNewMainFile(null)
                      if (data?.mainFile) setDeletedFileIds(prev => prev.filter(id => id !== data.mainFile!.id))
                    }}>
                      <ActionsImage src={RemoveIcon} />
                    </StyledIconButton>
                  </MainFileCard>
                )}

                <FieldWrapper>
                  <UploadZone
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => {
                      e.preventDefault()
                      const file = e.dataTransfer.files?.[0]
                      if (file) replaceMainFile(file)
                    }}
                  >
                    <HiddenInput
                      ref={mainFileRef}
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={e => { if (e.target.files?.[0]) replaceMainFile(e.target.files[0]) }}
                    />
                    <CloudUpload style={{ color: '#94A3B8', fontSize: 28 }} />
                    <UploadText>
                      {data.mainFile ? t`upload_main_file` : t`upload_click_or_drag`}
                    </UploadText>
                  </UploadZone>
                </FieldWrapper>
              </NewCategoryContainerColumn>

              {/* Attachments */}
              <NewCategoryContainerColumn style={{ marginTop: '24px' }}>
                <FileSectionTitle>
                  <ImageHeader src={SecondaryFileIcon} style={{ width: 18, height: 18 }} />
                  {t`attachments`}
                </FileSectionTitle>

                {data.attachments.filter(f => !deletedFileIds.includes(f.id)).map(file => (
                  <SecondaryFileCard key={file.id}>
                    <SecondaryFileIconBox>
                      <ImageHeader src={SecondaryFileIcon} />
                    </SecondaryFileIconBox>
                    <SecondaryFileInfo>
                      <SecondaryFileName>{cleanImportedText(file.fileName)}</SecondaryFileName>
                      <SecondaryFileSize>{formatFileSize(file.fileSize)}</SecondaryFileSize>
                    </SecondaryFileInfo>
                    <StyledIconButton onClick={() => setDeletedFileIds(prev => [...prev, file.id])}>
                      <ActionsImage src={RemoveIcon} />
                    </StyledIconButton>
                  </SecondaryFileCard>
                ))}

                {newAttachments.map((file, i) => (
                  <SecondaryFileCard key={i} style={{ border: '1px solid #B9F8CF', background: '#F0FDF4' }}>
                    <SecondaryFileIconBox>
                      <ImageHeader src={SecondaryFileIcon} style={{ filter: 'invert(46%) sepia(98%) saturate(432%) hue-rotate(100deg) brightness(95%) contrast(103%)' }} />
                    </SecondaryFileIconBox>
                    <SecondaryFileInfo>
                      <SecondaryFileName>{file.name} <NewBadge>{t`badge_new`}</NewBadge></SecondaryFileName>
                      <SecondaryFileSize>{formatFileSize(file.size)}</SecondaryFileSize>
                    </SecondaryFileInfo>
                    <StyledIconButton onClick={() => setNewAttachments(prev => prev.filter((_, j) => j !== i))}>
                      <ActionsImage src={RemoveIcon} />
                    </StyledIconButton>
                  </SecondaryFileCard>
                ))}

                <FieldWrapper>
                  <UploadZone
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => {
                      e.preventDefault()
                      const files = Array.from(e.dataTransfer.files || [])
                      if (files.length) setNewAttachments(prev => [...prev, ...files])
                    }}
                  >
                    <HiddenInput
                      ref={attachmentsRef}
                      type="file"
                      multiple
                      onChange={e => setNewAttachments(prev => [...prev, ...Array.from(e.target.files || [])])}
                    />
                    <CloudUpload style={{ color: '#94A3B8', fontSize: 28 }} />
                    <UploadText>{t`upload_more_files`}</UploadText>
                  </UploadZone>
                </FieldWrapper>
              </NewCategoryContainerColumn>

              </LockableFields>

              {/* Private / Public - outside the lock: it is the only thing that stays editable
                  once the protocol is published (Approved). Still locked while Pending Approval. */}
              <VisibilityBar>
                <VisibilityButton $public={isPublic} disabled={fieldsLocked && !isLocked} onClick={() => setIsPublic(prev => !prev)}>
                  {isPublic ? <LockOpen style={{ fontSize: 18 }} /> : <Lock style={{ fontSize: 18 }} />} {isPublic ? t`visibility_public` : t`visibility_private`}
                </VisibilityButton>
                <VisibilityText>
                  {isPublic ? t`visibility_public_hint` : t`visibility_private_hint`}
                </VisibilityText>
              </VisibilityBar>
              </FormColumn>
            </NewCategoryBody>
          )}
        </ProtocolEditContainer>

        <ProtocolContainerActions style={{ paddingRight: 0 }}>
          <CloseButton onClick={handleClose}>{t`close`}</CloseButton>
          {canPublishProtocol && renderPublishButton()}
          <HeaderDialogButton onClick={handleSave} disabled={saving || publishing || !title.trim()}>
            {saving ? <Spinner size={16} /> : <><ImageHeaderBlue src={SaveIcon} /> {t`save_changes`}</>}
          </HeaderDialogButton>
        </ProtocolContainerActions>
      </DialogContent>
      </>
      )}
    </Dialog>
  )
}

export default observer(ProtocolEditModal)
